/**
 * The upload queue state machine and its run driver.
 *
 * The Imports page lets the owner pick many report files of one kind at once
 * and imports them one at a time. The API stays one file per request, so all
 * the batching lives here: which file goes next, what a row shows while it is
 * in flight, how a result lands on its row, and the async loop that walks the
 * queue. Keeping it out of the component makes it a set of small pure functions
 * plus one driver that can be unit-tested without a browser or a server.
 *
 * Every function takes the current list and returns a new list; nothing is
 * mutated in place, so React state updates stay honest.
 */

/** 50 MB, matching the API's per-file limit exactly. */
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export type QueueStatus =
  'queued' | 'uploading' | 'imported' | 'duplicate' | 'failed' | 'too_large';

/**
 * How a kind carries dates: none, a start/end range per file (the business
 * report), or a single report day per file (snapshots and the listing report,
 * which otherwise all land on today and merge).
 */
export type DatePolicy = 'none' | 'range' | 'day';

/** The load counts the API answers for a file it imported. */
export interface ImportCounts {
  inserted: number;
  updated: number;
  skipped: number;
  /** All Listings only: SKUs this full file marked no longer listed. */
  delisted?: number;
  /** All Listings only: no-longer-listed SKUs this file carries again. */
  relisted?: number;
  /** All Listings only: SKUs the file leaves out that were not marked. */
  absent?: number;
}

/**
 * The presence-sweep half of a listings import's counts, as words ("2 no longer
 * listed, 1 listed again, 3 absent, not marked"); null when the import did not
 * sweep (any other kind) or found nothing.
 */
export function listingSweepText(
  delisted: number | null | undefined,
  relisted: number | null | undefined,
  absent?: number | null,
): string | null {
  const parts: string[] = [];
  if (delisted) parts.push(`${delisted.toLocaleString('en-US')} no longer listed`);
  if (relisted) parts.push(`${relisted.toLocaleString('en-US')} listed again`);
  if (absent) parts.push(`${absent.toLocaleString('en-US')} absent, not marked`);
  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * The minimum a file must expose for the queue's logic. The browser's `File`
 * satisfies it; a test can pass a plain object. The page keeps the real `File`
 * here to hand to `FormData` when it uploads the row.
 */
export interface FileLike {
  readonly name: string;
  readonly size: number;
  /** Epoch ms of last modification, used to guess a report day. */
  readonly lastModified?: number;
}

/** One row in the queue table. */
export interface QueueItem<F extends FileLike = FileLike> {
  /** Stable within one page session; used as the React key and the update key. */
  id: string;
  file: F;
  name: string;
  size: number;
  status: QueueStatus;
  /** YYYY-MM-DD, or '' when unset. */
  periodStart: string;
  periodEnd: string;
  /** Set once the file is imported. */
  counts: ImportCounts | null;
  rowCount: number | null;
  /** The failure message, for a failed row. */
  error: string | null;
  /** The server error code, for a failed row (e.g. a replaceable conflict). */
  code: string | null;
  /** The earlier import's date (YYYY-MM-DD), for a duplicate row. */
  duplicateDate: string | null;
  /**
   * The kind a wrong-kind refusal says the file really is, so the row can offer
   * to re-queue it under that kind. Kept as a plain string to keep this module
   * free of the report-kind union.
   */
  suggestedKind: string | null;
  /**
   * A per-row override of the batch's kind, set by "Upload as …". Only this row
   * uploads under it; the chooser and the other rows are untouched, so a leftover
   * queued row keeps the kind it was added under. Null means the batch kind.
   */
  overrideKind: string | null;
  /** Neutral notices from the server for an imported row (dropped columns, say). */
  warnings: string[];
}

/** What an upload attempt produced, ready to fold onto its row. */
export interface UploadResult {
  kind: 'imported' | 'duplicate' | 'failed';
  counts?: ImportCounts | null;
  rowCount?: number | null;
  error?: string | null;
  code?: string | null;
  duplicateDate?: string | null;
  suggestedKind?: string | null;
  warnings?: string[];
}

export interface AddOptions {
  /** How this kind carries dates. */
  datePolicy: DatePolicy;
  /** Produces a fresh unique id for each added row. */
  makeId: () => string;
  /** Guesses a range from a file name (range kinds). */
  parsePeriod?: (name: string) => { start: string; end: string } | null;
  /** Guesses a single day from a file name (day kinds). */
  parseDate?: (name: string) => string | null;
  /** Turns an epoch-ms timestamp into a YYYY-MM-DD in the viewer's zone. */
  zoneDay?: (ms: number) => string;
  /** Today, YYYY-MM-DD, the last-resort report day for a day kind. */
  today?: string;
}

/** A row whose outcome is settled — it will not change on its own again. */
export function isTerminal(status: QueueStatus): boolean {
  return (
    status === 'imported' || status === 'duplicate' || status === 'failed' || status === 'too_large'
  );
}

/** Appends the picked files as new rows, marking oversized ones and prefilling dates. */
export function addFiles<F extends FileLike>(
  existing: QueueItem<F>[],
  incoming: readonly F[],
  options: AddOptions,
): QueueItem<F>[] {
  return [...existing, ...incoming.map((file) => newItem(file, options))];
}

function newItem<F extends FileLike>(file: F, options: AddOptions): QueueItem<F> {
  const tooLarge = file.size > MAX_FILE_BYTES;
  let periodStart = '';
  let periodEnd = '';
  if (!tooLarge) {
    if (options.datePolicy === 'range') {
      const guess = options.parsePeriod?.(file.name) ?? null;
      periodStart = guess?.start ?? '';
      periodEnd = guess?.end ?? '';
    } else if (options.datePolicy === 'day') {
      const day = guessDay(file, options);
      periodStart = day;
      periodEnd = day;
    }
  }
  return {
    id: options.makeId(),
    file,
    name: file.name,
    size: file.size,
    status: tooLarge ? 'too_large' : 'queued',
    periodStart,
    periodEnd,
    counts: null,
    rowCount: null,
    error: null,
    code: null,
    duplicateDate: null,
    suggestedKind: null,
    overrideKind: null,
    warnings: [],
  };
}

/**
 * Re-queues a failed row under a new kind (the wrong-kind suggestion the user
 * accepted). Only this row changes; the batch chooser and the other rows are
 * left alone, so a leftover queued row keeps the kind it was added under.
 */
export function setOverrideKind<F extends FileLike>(
  items: QueueItem<F>[],
  id: string,
  kind: string,
): QueueItem<F>[] {
  return items.map((item) =>
    item.id === id && item.status === 'failed'
      ? {
          ...item,
          status: 'queued',
          overrideKind: kind,
          error: null,
          code: null,
          suggestedKind: null,
          counts: null,
          rowCount: null,
          warnings: [],
        }
      : item,
  );
}

/** The best guess of a day kind's report date: file name, else mtime, else today. */
function guessDay(file: FileLike, options: AddOptions): string {
  const fromName = options.parseDate?.(file.name);
  if (fromName) return fromName;
  if (file.lastModified !== undefined && options.zoneDay) return options.zoneDay(file.lastModified);
  return options.today ?? '';
}

/**
 * Re-guesses dates on still-queued rows the uploader has not filled in, for
 * when the kind changes after files are already in the queue. A row already
 * carrying a date is left alone.
 */
export function prefillPeriods<F extends FileLike>(
  items: QueueItem<F>[],
  options: AddOptions,
): QueueItem<F>[] {
  return items.map((item) => {
    if (item.status !== 'queued' || item.periodStart || item.periodEnd) return item;
    if (options.datePolicy === 'range') {
      const guess = options.parsePeriod?.(item.name);
      if (!guess) return item;
      return { ...item, periodStart: guess.start, periodEnd: guess.end };
    }
    if (options.datePolicy === 'day') {
      const day = guessDay(item.file, options);
      if (!day) return item;
      return { ...item, periodStart: day, periodEnd: day };
    }
    return item;
  });
}

/** Sets the period on one queued row (a range edit). */
export function setPeriod<F extends FileLike>(
  items: QueueItem<F>[],
  id: string,
  period: { start?: string; end?: string },
): QueueItem<F>[] {
  return items.map((item) =>
    item.id === id
      ? {
          ...item,
          periodStart: period.start ?? item.periodStart,
          periodEnd: period.end ?? item.periodEnd,
        }
      : item,
  );
}

/** Sets a single report day on one queued row (both start and end). */
export function setReportDate<F extends FileLike>(
  items: QueueItem<F>[],
  id: string,
  day: string,
): QueueItem<F>[] {
  return items.map((item) =>
    item.id === id ? { ...item, periodStart: day, periodEnd: day } : item,
  );
}

/** Removes a row — anything except one currently uploading. */
export function removeItem<F extends FileLike>(items: QueueItem<F>[], id: string): QueueItem<F>[] {
  return items.filter((item) => !(item.id === id && item.status !== 'uploading'));
}

/** Puts a failed row back in the queue to try again. */
export function retryItem<F extends FileLike>(items: QueueItem<F>[], id: string): QueueItem<F>[] {
  return items.map((item) =>
    item.id === id && item.status === 'failed'
      ? {
          ...item,
          status: 'queued',
          error: null,
          code: null,
          counts: null,
          rowCount: null,
          suggestedKind: null,
          warnings: [],
        }
      : item,
  );
}

/** Empties the whole queue (the "Clear" button once a run is done). */
export function clearAll<F extends FileLike>(_items: QueueItem<F>[]): QueueItem<F>[] {
  return [];
}

/** Whether a row's range is valid: both ends present and start <= end. */
export function hasValidPeriod(item: { periodStart: string; periodEnd: string }): boolean {
  if (!item.periodStart || !item.periodEnd) return false;
  return item.periodStart <= item.periodEnd;
}

/**
 * The ids of queued day-kind rows whose report date collides with another
 * queued row, so the UI can flag them. Two files for the same day would merge
 * into one fictitious snapshot, so this blocks the start.
 */
export function duplicateDayIds<F extends FileLike>(items: QueueItem<F>[]): Set<string> {
  const byDay = new Map<string, string[]>();
  for (const item of items) {
    if (item.status !== 'queued' || !item.periodStart) continue;
    const ids = byDay.get(item.periodStart) ?? [];
    ids.push(item.id);
    byDay.set(item.periodStart, ids);
  }
  const flagged = new Set<string>();
  for (const ids of byDay.values()) {
    if (ids.length > 1) for (const id of ids) flagged.add(id);
  }
  return flagged;
}

/**
 * Whether the queue can be started: at least one row is queued, and every
 * queued row satisfies the kind's date policy. Oversized rows never upload, so
 * they do not block the start.
 */
export function readyToStart<F extends FileLike>(
  items: QueueItem<F>[],
  datePolicy: DatePolicy,
): boolean {
  const queued = items.filter((item) => item.status === 'queued');
  if (queued.length === 0) return false;
  if (datePolicy === 'range') return queued.every(hasValidPeriod);
  if (datePolicy === 'day') {
    if (duplicateDayIds(items).size > 0) return false;
    return queued.every((item) => item.periodStart !== '');
  }
  return true;
}

/**
 * The next file to upload: the first still-queued row, or null when a stop has
 * been requested or nothing is left.
 */
export function nextUpload<F extends FileLike>(
  items: QueueItem<F>[],
  options: { stopRequested?: boolean } = {},
): QueueItem<F> | null {
  if (options.stopRequested) return null;
  return items.find((item) => item.status === 'queued') ?? null;
}

/** Marks a queued row as in flight. */
export function markUploading<F extends FileLike>(
  items: QueueItem<F>[],
  id: string,
): QueueItem<F>[] {
  return items.map((item) =>
    item.id === id && item.status === 'queued' ? { ...item, status: 'uploading' } : item,
  );
}

/** Folds an upload result onto its row. */
export function applyResult<F extends FileLike>(
  items: QueueItem<F>[],
  id: string,
  result: UploadResult,
): QueueItem<F>[] {
  return items.map((item) => {
    if (item.id !== id) return item;
    return {
      ...item,
      status: result.kind,
      counts: result.counts ?? null,
      rowCount: result.rowCount ?? null,
      error: result.error ?? null,
      code: result.code ?? null,
      duplicateDate: result.duplicateDate ?? null,
      suggestedKind: result.suggestedKind ?? null,
      warnings: result.warnings ?? [],
    };
  });
}

/**
 * Walks the queue, uploading one file at a time in order. Marks each row in
 * flight, awaits its result, folds it on, and reports the new list through
 * `onChange` after every step. Stops before the next file when `shouldStop`
 * returns true; a failure on one row does not stop the rest. Rows that are not
 * `queued` (oversized, already finished, in flight) are never uploaded, so
 * calling it again while a run is on does nothing. Returns the final list.
 */
export async function runQueue<F extends FileLike>(
  items: QueueItem<F>[],
  uploadFn: (item: QueueItem<F>) => Promise<UploadResult>,
  shouldStop: () => boolean,
  onChange: (items: QueueItem<F>[]) => void,
): Promise<QueueItem<F>[]> {
  let current = items;
  const queue = current.filter((item) => item.status === 'queued');
  for (const item of queue) {
    if (shouldStop()) break;
    current = markUploading(current, item.id);
    onChange(current);
    const result = await uploadFn(item);
    current = applyResult(current, item.id, result);
    onChange(current);
  }
  return current;
}

export interface QueueTotals {
  total: number;
  queued: number;
  uploading: number;
  imported: number;
  duplicate: number;
  failed: number;
  tooLarge: number;
  /** Rows whose outcome is settled. */
  finished: number;
}

/** Counts every row by status, for the running tally and the closing line. */
export function totals<F extends FileLike>(items: QueueItem<F>[]): QueueTotals {
  const t: QueueTotals = {
    total: items.length,
    queued: 0,
    uploading: 0,
    imported: 0,
    duplicate: 0,
    failed: 0,
    tooLarge: 0,
    finished: 0,
  };
  for (const item of items) {
    switch (item.status) {
      case 'queued':
        t.queued += 1;
        break;
      case 'uploading':
        t.uploading += 1;
        break;
      case 'imported':
        t.imported += 1;
        break;
      case 'duplicate':
        t.duplicate += 1;
        break;
      case 'failed':
        t.failed += 1;
        break;
      case 'too_large':
        t.tooLarge += 1;
        break;
    }
    if (isTerminal(item.status)) t.finished += 1;
  }
  return t;
}
